/**
 * MASTER — HISTORICAL_EDITORIAL / «Историческая справка».
 *
 * Reusable across every Digital Editions surface — paid Volumes (Almond
 * pilot, future volumes), Open Library, paid courses, Gingerbread Workshop
 * and future editorial collections. NOTHING in this file is Almond-specific,
 * volume-specific, product-specific, access-specific or route-specific.
 *
 * Storage:
 *   The `BlockContent` schema in the backend does not (yet) expose a
 *   dedicated top-level key for this master and this sprint explicitly
 *   forbids backend/schema changes. The schema does expose a sanctioned
 *   future-proof escape hatch — `content.extra: Dict[str, Any]` — which is
 *   exactly the intended slot for masters not yet materialised at the
 *   backend layer. We therefore persist the structured payload under
 *   `content.extra.historical_editorial` and read it back with a graceful
 *   fallback to `content.historical_editorial` in case the schema gains a
 *   dedicated key in a future backend sprint.
 *
 *   Every unknown or legacy top-level key of `block.content` is preserved
 *   verbatim on save (headline / lead / body / quote / signature / hero
 *   images stay untouched). On first read, useful legacy fields seed
 *   sensible defaults into the working local shape only — the on-disk
 *   legacy fields are never mutated.
 *
 * Shared primitives reused from HeroMaster / CropModal — no second media
 * workflow is created here.
 */
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  ArrowUp, ArrowDown, Trash2, Plus, ScrollText,
} from "lucide-react";
import CropModal from "@/components/editorial/CropModal";
import {
  ImageSlot,
  MediaLibraryPicker,
} from "@/components/editorial/HeroMaster";
import { resolveMediaUrl } from "@/lib/editorialMedia.mjs";
import { editorialImageStyle } from "@/lib/editorialReaderLayout.mjs";

// ---------------------------------------------------------------- helpers --

const genId = (p = "id") =>
  `${p}${Date.now().toString(36)}${Math.floor(Math.random() * 1e4)}`;

const emptyImage = () => ({
  media_id: null,
  url: null,
  alt: "",
  caption: "",
  focal_x: 0.5,
  focal_y: 0.5,
  zoom: 1,
});

// Normalises `content.historical_editorial` — never mutates the input.
// Legacy top-level content fields (`headline`, `lead`, `body`, `quote`,
// `signature`, `hero_url`, `hero_mobile_url`) seed the working shape the
// first time the block is opened in the new editor. They remain in the
// stored document unchanged so no data is ever lost.
function readHistorical(rawContent) {
  const c = rawContent || {};
  // Prefer a future dedicated top-level key; fall back to the sanctioned
  // `content.extra.historical_editorial` slot that we actually persist to
  // today (the backend `BlockContent` schema drops unknown top-level keys
  // via Pydantic default, but `extra: Dict[str, Any]` is explicitly free-form).
  const fromExtra = (c.extra && typeof c.extra === "object" && c.extra.historical_editorial)
    ? c.extra.historical_editorial : null;
  const h = (c.historical_editorial && typeof c.historical_editorial === "object")
    ? c.historical_editorial
    : (fromExtra && typeof fromExtra === "object" ? fromExtra : {});

  // legacy body → first text block, only when nothing structured exists yet
  const legacyText = typeof c.body === "string" ? c.body.trim() : "";
  const seededBlocks = Array.isArray(h.text_blocks) && h.text_blocks.length
    ? h.text_blocks.map((b) => ({
        id:   b.id   || genId("hp"),
        text: b.text || "",
      }))
    : legacyText
      ? [{ id: genId("hp"), text: legacyText }]
      : [{ id: genId("hp"), text: "" }];

  const readImg = (img, legacyUrl) => ({
    media_id: img?.media_id ?? null,
    url:      img?.url      ?? legacyUrl ?? null,
    alt:      img?.alt      ?? "",
    caption:  img?.caption  ?? "",
    focal_x:  img?.focal_x  ?? 0.5,
    focal_y:  img?.focal_y  ?? 0.5,
    zoom:     img?.zoom     ?? 1,
  });

  return {
    eyebrow:   h.eyebrow    ?? c.eyebrow  ?? "",
    title:     h.title      ?? c.headline ?? "",
    subtitle:  h.subtitle   ?? c.lead     ?? "",
    period:    h.period     ?? "",
    location:  h.location   ?? "",
    text_blocks: seededBlocks,
    main_image:   readImg(h.main_image,   c.hero_url),
    mobile_image: readImg(h.mobile_image, c.hero_mobile_url),
    quote:        h.quote        ?? c.quote     ?? "",
    quote_source: h.quote_source ?? c.signature ?? "",
  };
}

// ---------------------------------------------------------------- Editor --

export function HistoricalEditorialEditor({ block, onPatch }) {
  const h = useMemo(() => readHistorical(block.content), [block.content]);

  // Merge into existing content — legacy top-level keys stay intact.
  // We persist under `content.extra.historical_editorial` (see file header).
  const patch = (delta) => {
    const prevContent = block.content || {};
    const prevExtra   = (prevContent.extra && typeof prevContent.extra === "object")
      ? prevContent.extra : {};
    onPatch({
      content: {
        ...prevContent,
        extra: {
          ...prevExtra,
          historical_editorial: { ...h, ...delta },
        },
      },
    });
  };

  const [mainLibraryOpen, setMainLibraryOpen] = useState(false);
  const [mobileLibraryOpen, setMobileLibraryOpen] = useState(false);
  const [mainCropOpen, setMainCropOpen]     = useState(false);
  const [mobileCropOpen, setMobileCropOpen] = useState(false);

  const setMain   = (delta) => patch({ main_image:   { ...h.main_image,   ...delta } });
  const setMobile = (delta) => patch({ mobile_image: { ...h.mobile_image, ...delta } });

  // -- ordered text blocks --
  const setBlocks = (blocks) => patch({ text_blocks: blocks });
  const addBlock = () => setBlocks([...h.text_blocks, { id: genId("hp"), text: "" }]);
  const removeBlock = (i) => {
    const next = [...h.text_blocks]; next.splice(i, 1);
    setBlocks(next.length ? next : [{ id: genId("hp"), text: "" }]);
  };
  const moveBlock = (i, dir) => {
    const next = [...h.text_blocks];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setBlocks(next);
  };
  const patchBlock = (i, delta) => {
    const next = [...h.text_blocks];
    next[i] = { ...next[i], ...delta };
    setBlocks(next);
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4"
         data-testid="historical-editor">
      {/* LEFT — text */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Историческая справка · текст</div>

        <div>
          <label className="block text-[11px] opacity-60 mb-1">Метка раздела · необязательно</label>
          <Input value={h.eyebrow}
                 onChange={(e) => patch({ eyebrow: e.target.value })}
                 placeholder="ИСТОРИЧЕСКАЯ СПРАВКА"
                 data-testid="historical-field-eyebrow" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Заголовок</label>
          <Input value={h.title}
                 onChange={(e) => patch({ title: e.target.value })}
                 className="font-serif text-lg"
                 data-testid="historical-field-title" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Подзаголовок · лид · необязательно</label>
          <textarea rows={2} value={h.subtitle}
                    onChange={(e) => patch({ subtitle: e.target.value })}
                    className="w-full border rounded px-2 py-1.5 text-sm italic font-serif"
                    data-testid="historical-field-subtitle" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] opacity-60 mb-1">Период · необязательно</label>
            <Input value={h.period}
                   onChange={(e) => patch({ period: e.target.value })}
                   placeholder="XVIII–XIX века"
                   data-testid="historical-field-period" />
          </div>
          <div>
            <label className="block text-[11px] opacity-60 mb-1">Место · необязательно</label>
            <Input value={h.location}
                   onChange={(e) => patch({ location: e.target.value })}
                   placeholder="Генуя · Париж"
                   data-testid="historical-field-location" />
          </div>
        </div>

        <div className="pt-2">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest opacity-70">Абзацы</div>
            <button type="button" onClick={addBlock}
              className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
              data-testid="historical-add-block">
              <Plus className="w-3.5 h-3.5" /> Добавить абзац
            </button>
          </div>
          <div className="space-y-2">
            {h.text_blocks.map((b, i) => (
              <div key={b.id} className="rounded-lg border hairline bg-cream/40 p-3 space-y-2"
                   data-testid={`historical-block-${i}`}>
                <div className="flex items-center gap-2">
                  <div className="text-[10px] uppercase tracking-widest opacity-60 shrink-0 font-mono">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="flex-1" />
                  <div className="flex items-center gap-1 shrink-0">
                    <button type="button" onClick={() => moveBlock(i, -1)} disabled={i === 0}
                      className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                      title="Выше" data-testid={`historical-block-up-${i}`}>
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => moveBlock(i, +1)} disabled={i === h.text_blocks.length - 1}
                      className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                      title="Ниже" data-testid={`historical-block-down-${i}`}>
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => removeBlock(i)}
                      className="p-1 opacity-60 hover:opacity-100 text-red-600"
                      title="Удалить" data-testid={`historical-block-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <textarea rows={4} value={b.text}
                          onChange={(e) => patchBlock(i, { text: e.target.value })}
                          className="w-full border rounded px-2 py-1.5 text-sm font-serif leading-relaxed"
                          placeholder="Абзац исторической справки…"
                          data-testid={`historical-block-text-${i}`} />
              </div>
            ))}
          </div>
        </div>

        <div className="pt-2 border-t border-espresso/10">
          <div className="text-[10px] uppercase tracking-widest opacity-70 mb-2">Цитата · необязательно</div>
          <textarea rows={2} value={h.quote}
                    onChange={(e) => patch({ quote: e.target.value })}
                    className="w-full border rounded px-2 py-1.5 text-sm italic font-serif"
                    placeholder="Одна короткая цитата, поддерживающая раздел"
                    data-testid="historical-field-quote" />
          <div className="mt-2">
            <label className="block text-[11px] opacity-60 mb-1">Источник · автор · необязательно</label>
            <Input value={h.quote_source}
                   onChange={(e) => patch({ quote_source: e.target.value })}
                   placeholder="Автор, издание, год"
                   data-testid="historical-field-quote-source" />
          </div>
        </div>
      </div>

      {/* RIGHT — images */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Изображения</div>

        <div>
          <div className="text-[11px] opacity-60 mb-2">Основное изображение · для компьютера</div>
          <ImageSlot
            label=""
            url={h.main_image.url}
            focalX={h.main_image.focal_x} focalY={h.main_image.focal_y} zoom={h.main_image.zoom}
            onFile={(m) => setMain({ media_id: m.id, url: m.file_url })}
            onClear={() => setMain({ media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
            onOpenLibrary={() => setMainLibraryOpen(true)}
            onOpenCrop={() => setMainCropOpen(true)}
            testid="historical-main-image" />
          <div className="mt-2 grid grid-cols-1 gap-2">
            <Input placeholder="Описание изображения · необязательно"
                   value={h.main_image.alt}
                   onChange={(e) => setMain({ alt: e.target.value })}
                   data-testid="historical-main-alt" />
            <Input placeholder="Подпись под изображением · необязательно"
                   value={h.main_image.caption}
                   onChange={(e) => setMain({ caption: e.target.value })}
                   data-testid="historical-main-caption" />
          </div>
        </div>

        <div className="pt-2 border-t border-espresso/10">
          <div className="text-[11px] opacity-60 mb-2">
            Отдельное мобильное изображение · необязательно
            <span className="ml-1 opacity-70">(если пусто — на мобильном используется основное)</span>
          </div>
          <ImageSlot
            label=""
            url={h.mobile_image.url}
            focalX={h.mobile_image.focal_x} focalY={h.mobile_image.focal_y} zoom={h.mobile_image.zoom}
            onFile={(m) => setMobile({ media_id: m.id, url: m.file_url })}
            onClear={() => setMobile({ media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
            onOpenLibrary={() => setMobileLibraryOpen(true)}
            onOpenCrop={() => setMobileCropOpen(true)}
            testid="historical-mobile-image" />
          <div className="mt-2 grid grid-cols-1 gap-2">
            <Input placeholder="Описание изображения · необязательно"
                   value={h.mobile_image.alt}
                   onChange={(e) => setMobile({ alt: e.target.value })}
                   data-testid="historical-mobile-alt" />
            <Input placeholder="Подпись под изображением · необязательно"
                   value={h.mobile_image.caption}
                   onChange={(e) => setMobile({ caption: e.target.value })}
                   data-testid="historical-mobile-caption" />
          </div>
        </div>
      </div>

      <MediaLibraryPicker
        open={mainLibraryOpen}
        onClose={() => setMainLibraryOpen(false)}
        onPick={(m) => { setMain({ media_id: m.id, url: m.file_url || m.url }); setMainLibraryOpen(false); }} />
      <MediaLibraryPicker
        open={mobileLibraryOpen}
        onClose={() => setMobileLibraryOpen(false)}
        onPick={(m) => { setMobile({ media_id: m.id, url: m.file_url || m.url }); setMobileLibraryOpen(false); }} />
      <CropModal
        open={mainCropOpen}
        url={h.main_image.url}
        focalX={h.main_image.focal_x}
        focalY={h.main_image.focal_y}
        zoom={h.main_image.zoom}
        onClose={() => setMainCropOpen(false)}
        onSave={({ focal_x, focal_y, zoom }) => { setMain({ focal_x, focal_y, zoom }); setMainCropOpen(false); }} />
      <CropModal
        open={mobileCropOpen}
        url={h.mobile_image.url}
        focalX={h.mobile_image.focal_x}
        focalY={h.mobile_image.focal_y}
        zoom={h.mobile_image.zoom}
        onClose={() => setMobileCropOpen(false)}
        onSave={({ focal_x, focal_y, zoom }) => { setMobile({ focal_x, focal_y, zoom }); setMobileCropOpen(false); }} />
    </div>
  );
}

// ---------------------------------------------------------------- Render --

function EditorialImage({ img, mode = "desktop" }) {
  if (!img?.url) return null;
  const maxH = mode === "mobile" ? 420 : 680;
  return (
    <figure className="w-full" data-testid="historical-image-figure">
      <div className="overflow-hidden bg-neutral-100 aspect-[4/5]"
           style={{ maxHeight: maxH }}>
        <img
          src={resolveMediaUrl(img.url)}
          alt={img.alt || ""}
          className="h-full w-full"
          style={editorialImageStyle(img)}
        />
      </div>
      {img.caption && (
        <figcaption className="mt-3 text-[12px] italic font-serif leading-[1.5] max-w-[36ch]"
                    style={{ color: "#5B4326" }}>
          {img.caption}
        </figcaption>
      )}
    </figure>
  );
}

// A single small uppercase meta pill — used for period / location.
function MetaSlot({ label, value }) {
  return (
    <div className="inline-flex items-baseline gap-2">
      <span className="text-[9px] tracking-[0.28em] uppercase opacity-70" style={{ color: "#8A6A46" }}>
        {label}
      </span>
      <span className="font-serif text-[13px]" style={{ color: "#3E2E20" }}>
        {value}
      </span>
    </div>
  );
}

export function HistoricalEditorialRender({ block, mode = "desktop" }) {
  const h = useMemo(() => readHistorical(block.content), [block.content]);
  const isMobile = mode === "mobile";

  // Mobile prefers a dedicated mobile image and gracefully falls back to
  // the main desktop image when the mobile slot is empty.
  const mobileImage = h.mobile_image.url ? h.mobile_image : h.main_image;
  const activeImage = isMobile ? mobileImage : h.main_image;
  const hasImage = !!(activeImage && activeImage.url);

  const paragraphs = (h.text_blocks || []).filter((b) => b.text && b.text.trim());
  const hasQuote = !!(h.quote && h.quote.trim());
  const hasPeriod   = !!(h.period && h.period.trim());
  const hasLocation = !!(h.location && h.location.trim());

  const MetaRow = (hasPeriod || hasLocation) ? (
    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 mb-4"
         data-testid="historical-meta-row">
      {hasPeriod   && <MetaSlot label="Период" value={h.period} />}
      {hasLocation && <MetaSlot label="Место"  value={h.location} />}
    </div>
  ) : null;

  const Eyebrow = h.eyebrow ? (
    <div className="inline-flex items-center gap-2 mb-4 text-[10px] tracking-[0.28em] uppercase"
         style={{ color: "#8A6A46" }}>
      <ScrollText className="w-3.5 h-3.5" />
      {h.eyebrow}
    </div>
  ) : (
    <div className="inline-flex items-center gap-2 mb-4 text-[10px] tracking-[0.28em] uppercase"
         style={{ color: "#8A6A46" }}>
      <ScrollText className="w-3.5 h-3.5" />
      Историческая справка
    </div>
  );

  const Title = h.title ? (
    <h2 className="min-w-0 break-words font-serif leading-[1.08] [overflow-wrap:anywhere]"
        style={{
          fontSize: isMobile ? "clamp(25px, 6vw, 32px)" : "clamp(32px, 3.2vw, 46px)",
          color: "#2A1D14",
          letterSpacing: "-0.005em",
        }}>
      {h.title}
    </h2>
  ) : null;

  const Subtitle = h.subtitle ? (
    <p className="mt-3 font-serif italic text-[16px] leading-[1.55] max-w-[52ch]"
       style={{ color: "#5B4326" }}>
      {h.subtitle}
    </p>
  ) : null;

  const Rule = <div className="my-6 h-px w-16" style={{ background: "#A67B4A" }} aria-hidden />;

  const Paragraphs = paragraphs.length ? (
    <div className="space-y-4">
      {paragraphs.map((b) => (
        <p key={b.id}
           className="font-serif text-[16px] leading-[1.75] whitespace-pre-wrap max-w-[62ch]"
           style={{ color: "#2A1D14" }}
           data-testid={`historical-paragraph-${b.id}`}>
          {b.text}
        </p>
      ))}
    </div>
  ) : null;

  const Quote = hasQuote ? (
    <div className="mt-8 pl-4 border-l-2" style={{ borderColor: "#A67B4A" }}
         data-testid="historical-quote">
      <p className="font-serif italic text-[17px] leading-[1.6] max-w-[52ch]"
         style={{ color: "#3E2E20" }}>
        {`«${h.quote.trim().replace(/^[«"]|[»"]$/g, "")}»`}
      </p>
      {h.quote_source && (
        <div className="mt-2 text-[11px] tracking-[0.2em] uppercase" style={{ color: "#8A6A46" }}>
          — {h.quote_source}
        </div>
      )}
    </div>
  ) : null;

  return (
    <section className="w-full overflow-hidden"
             style={{ background: "#F4EBD8" }}
             data-testid={`historical-render-${block.id}-${mode}`}>
      <div className={`mx-auto px-6 sm:px-10 py-12 sm:py-16 ${isMobile ? "" : "max-w-[1200px]"}`}>
        {isMobile ? (
          <>
            {Eyebrow}
            {MetaRow}
            {Title}
            {Subtitle}
            {hasImage && (
              <div className="mt-8">
                <EditorialImage img={activeImage} mode="mobile" />
              </div>
            )}
            {paragraphs.length ? <div className="mt-8">{Paragraphs}</div> : null}
            {Quote}
          </>
        ) : hasImage ? (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,42fr)_minmax(0,58fr)] gap-12 lg:gap-16 items-start">
            <div>
              {Eyebrow}
              {MetaRow}
              {Title}
              {Subtitle}
              {Rule}
              {Paragraphs}
              {Quote}
            </div>
            <div>
              <EditorialImage img={activeImage} mode="desktop" />
            </div>
          </div>
        ) : (
          // No image on desktop — collapse to a single centred editorial
          // column, do NOT reserve a blank right column.
          <div className="mx-auto max-w-[720px]">
            {Eyebrow}
            {MetaRow}
            {Title}
            {Subtitle}
            {Rule}
            {Paragraphs}
            {Quote}
          </div>
        )}
      </div>
    </section>
  );
}
